import { Session } from 'src_common/sdk/session';
import { observable, action, computed, makeAutoObservable } from 'mobx';
import { SdkStaff, StaffAccountBasicDataType, StaffAccountWalletDataType } from 'src/AppState/sdk/staff';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { Amount } from 'src_common/common/amount/Amount';
import { PaymentMethodDropdownState } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/PaymentMethodDropdown.state';
import { validateAmountPattern, validateAmountRequire } from 'src/domains/players/shared/validatorsNew/validateRequire';
import { validateMaxAmount, validateMinAmount } from 'src/domains/players/shared/validatorsNew/validateMinMaxAmount';
import { CurrencyType, moneySymbol } from 'src_common/common/amount/website-money/currency';
import { ModelsState } from 'src/AppState/Models/ModelsState';
import { ApiResponseWrapper } from 'src/utils-mobx/ApiResponseWrapper';
import confirmation from 'src/utils/confirm.jsx';
import { amountViewToValue } from 'src/components/CustomersBar/CustomerTab/BankingTab/components/amountViewToValue';
import { YaspaState } from 'src/components/WithdrawForm/yaspa/YaspaSegment.state';
import { SavedCardInterface } from 'src_server/types/getSavedCards';
import { OPEN_BANKING_CARD } from 'src/components/PaymentMethodDropdown/PaymentMethodDropdown';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { AssigneeType } from 'src_server/types/paysafe/withdrawalsPaysafe';

export class WithdrawFormState {
    public readonly amount: FormInputState<string, Amount>;
    public readonly walletData: () => ApiResponseWrapper<StaffAccountWalletDataType | null>;
    public readonly basicData: () => ApiResponseWrapper<StaffAccountBasicDataType | null>;
    @observable public successMessage: string | null = null;
    @observable public errorMessage: string | null = null;

    public constructor(
        private readonly paymentMethodDropdownState: PaymentMethodDropdownState | null,
        private readonly sdkStaff: SdkStaff,
        private readonly session: Session,
        private readonly permisionsAllowed: Boolean,
        private readonly models: ModelsState,
        private readonly customerId: number,
        private readonly submitCallback: () => void,
        private readonly yaspaState: YaspaState,
        private readonly minWithdrawAmount: string,
        private readonly trpcClient: TrpcClient
    ) {
        this.walletData = (): ApiResponseWrapper<StaffAccountWalletDataType | null> =>
            this.models.getUserModel(this.customerId).walletData;
        this.basicData = (): ApiResponseWrapper<StaffAccountBasicDataType | null> =>
            this.models.getUserModel(this.customerId).basicData;

        this.amount = FormInputState.new('')
            .map(validateAmountRequire)
            .map(validateAmountPattern())
            .map(
                validateMinAmount(
                    new Amount(this.minWithdrawAmount),
                    () => moneySymbol(this.getCurrency),
                    'Minimum withdraw amount is'
                )
            )
            .map(
                validateMaxAmount(
                    () => this.getBackofficeWithdrawableBalance,
                    () => moneySymbol(this.getCurrency),
                    'Maximum withdraw amount is'
                )
            );
        makeAutoObservable(this);
    }

    public onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const inputValue = e.target.value;
        const formatValue = amountViewToValue(inputValue);
        this.amount.setValue(formatValue);
    };

    @action public handleSetAmount = (newAmount: Amount): void => {
        if (this.getBackofficeWithdrawableBalance.isLessThan(newAmount)) {
            this.amount.setValue(this.getBackofficeWithdrawableBalance.value);
            return;
        }

        this.amount.setValue(newAmount.value);
    };

    @action public handleSetMaximum = (): void => {
        this.amount.setValue(this.getBackofficeWithdrawableBalance.value);
    };

    @computed public get isButtonDisabled(): boolean {
        return (
            this.amount.result.value.type === 'error' ||
            this.paymentMethodDropdownState?.selectedPaymentMethod === undefined ||
            this.permisionsAllowed === false
        );
    }

    @computed public get getCurrency(): CurrencyType {
        return this.walletData().data?.currency ?? 'XTS';
    }

    @computed public get getBalance(): Amount {
        return new Amount(this.walletData().data?.balance ?? '0');
    }

    @computed public get getWithdrawableBalance(): Amount {
        return new Amount(this.walletData().data?.withdrawableBalance ?? '0');
    }

    @computed public get getBackofficeWithdrawableBalance(): Amount {
        return new Amount(this.walletData().data?.backofficeWithdrawableBalance ?? '0');
    }

    @computed public get getFailedWithdrawalsFunds(): Amount {
        return this.getBackofficeWithdrawableBalance.sub(this.getWithdrawableBalance);
    }

    @computed public get newBalance(): Amount {
        try {
            const basicInput = this.amount.result.value.type === 'ok' ? this.amount.result.value.data : new Amount('0');
            return this.getBalance.sub(basicInput);
        } catch (error) {
            return new Amount('0');
        }
    }

    private getAssignee = (): AssigneeType | null => {
        if (this.session.userId === null || this.session.loggedUserName === null) {
            return null;
        }

        return {
            name: this.session.loggedUserName,
            id: this.session.userId,
            type: 'account',
        };
    };

    @action public execWithdraw = async (paymentMethod: SavedCardInterface, amount: Amount): Promise<void> => {
        const response = await this.sdkStaff.withdraw({
            customer_id: this.customerId,
            requestBody: {
                amount: $appState.configState.precision.valueOldFormat(amount),
                channel: 'backoffice',
                payment_method: paymentMethod,
                assignee: this.getAssignee(),
                status: 'requested',
            },
        });

        if (response.status === '422') {
            this.errorMessage = response.errors[0]?.debugDetails ?? 'Unknown error occurred';
            return;
        }

        this.submitCallback();
    };

    @action public handleSubmitRealex = async (): Promise<void> => {
        try {
            if (
                this.amount.result.value.type === 'error' ||
                this.paymentMethodDropdownState?.selectedPaymentMethod === undefined
            ) {
                throw new Error();
            }

            this.successMessage = null;
            this.errorMessage = null;

            const paymentMethod = this.paymentMethodDropdownState.selectedPaymentMethod;
            const amount = this.amount.result.value.data;

            const result = await confirmation(
                `Are you sure that you want to withdraw ${amount.format(this.getCurrency)} ?`,
                true
            );

            //@ts-expect-error
            if (result === false) {
                return;
            }

            if (paymentMethod.id === OPEN_BANKING_CARD) {
                await this.yaspaState.submitDepositForm(() => amount.toFixed(2));
            } else {
                await this.execWithdraw(paymentMethod, amount);
            }

            this.successMessage = 'Withdrawal finished successfully.';
            this.amount.reset();
            return;
        } catch (e) {
            this.errorMessage = 'Unknown error occurred';
        }
    };

    @action public execWithdrawPaysafe = async (paymentMethod: SavedCardInterface, amount: string): Promise<void> => {
        const customer = this.basicData().data;

        if (customer === null) {
            this.errorMessage = 'No customer data';
            return;
        }

        const date = new Date().toUTCString();
        const withdrawalResponse = await this.trpcClient.client.paysafeRouter.withdrawalsPaysafe.mutate({
            customerId: customer.id,
            body: {
                merchantRefNum: `${customer.id}-${date}`,
                amount,
                currencyCode: customer.currency,
                paymentHandleToken: paymentMethod.providerReference ?? '',
                description: 'Withdrawal',
                channel: 'backoffice',
                assignee: this.getAssignee(),
                paymentMethod: paymentMethod,
            },
        });

        if (withdrawalResponse.responseStatus === 'error') {
            this.errorMessage = withdrawalResponse.data.errors[0]?.message ?? 'Unknown error occurred';
            return;
        }

        this.submitCallback();
    };

    @action public submitWithdrawFormPaysafe = async (): Promise<void> => {
        try {
            this.amount.setAsVisited();

            this.successMessage = null;
            this.errorMessage = null;
            if (this.paymentMethodDropdownState?.selectedPaymentMethod === undefined) {
                this.errorMessage = 'No selected payment method';
                return;
            }

            if (this.amount.result.value.type === 'error') {
                this.errorMessage = 'Wrong input value';
                return;
            }

            const result = await confirmation(
                `Are you sure that you want to withdraw ${this.amount.result.value.data.format(this.getCurrency)} ?`,
                true
            );

            //@ts-expect-error
            if (result === false) {
                return;
            }
            const paymentMethod = this.paymentMethodDropdownState.selectedPaymentMethod;
            const amount = this.amount.result.value.data;
            if (paymentMethod.id === OPEN_BANKING_CARD) {
                await this.yaspaState.submitDepositForm(() => amount.toFixed(2));
            } else {
                await this.execWithdrawPaysafe(this.paymentMethodDropdownState.selectedPaymentMethod, amount.value);
            }

            this.successMessage = 'Withdrawal finished successfully.';
            this.amount.reset();
        } catch (e) {
            console.log(e);
            this.errorMessage = 'Unknown error occurred';
        }
    };

    @action public handleSubmit = async (): Promise<void> => {
        if (this.paymentMethodDropdownState?.selectedPaymentMethod?.provider === 'realex') {
            await this.handleSubmitRealex();
        } else {
            await this.submitWithdrawFormPaysafe();
        }
    };
}
